import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Radio, Save } from "lucide-react";
import { API, authHeader } from "@/lib/api";

const Toggle = ({ checked, onChange, testid, label }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => onChange(!checked)}
    className={`relative w-14 h-8 rounded-full transition-colors ${checked ? "bg-green" : "bg-[#222]"}`}
    aria-label={label}
  >
    <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-black transition-transform ${checked ? "translate-x-6" : ""}`} />
  </button>
);

const SOCIAL_FIELDS = [
  ["youtube", "YouTube channel/live URL"],
  ["facebook", "Facebook page/live URL"],
  ["tiktok", "TikTok profile URL"],
  ["instagram", "Instagram profile URL"],
];

export const AdminLiveControl = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/live`).then(({ data }) => setS(data)).catch(() => toast.error("Couldn't load live settings."));
  }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setSocial = (k, v) => setS((p) => ({ ...p, socials: { ...(p.socials || {}), [k]: v } }));

  const save = async (override = {}) => {
    setSaving(true);
    try {
      const body = {
        is_live: s.is_live,
        title: s.title,
        platform: s.platform,
        stream_url: s.stream_url || "",
        show_chat: s.show_chat,
        offline_platform: s.offline_platform,
        offline_url: s.offline_url || "",
        socials: s.socials || {},
        ...override,
      };
      const { data } = await axios.put(`${API}/live`, body, { headers: authHeader() });
      setS(data);
      toast.success("Live settings saved.");
    } catch {
      toast.error("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggleLive = async () => {
    const next = !s.is_live;
    set("is_live", next);
    await save({ is_live: next });
  };

  if (!s) {
    return (
      <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-8 mb-14 flex items-center gap-3 text-zinc-500">
        <Loader2 className="animate-spin" size={18} /> Loading live settings…
      </div>
    );
  }

  return (
    <section data-testid="admin-live-control" className="border border-[#1a1a1a] bg-[#0a0a0a] p-6 md:p-8 mb-14">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <h2 className="font-display uppercase text-3xl text-white flex items-center gap-3">
          <Radio className={s.is_live ? "text-red-500 animate-pulse" : "text-green"} size={26} /> Live control
        </h2>
        <button
          data-testid="admin-golive-toggle"
          onClick={toggleLive}
          disabled={saving}
          className={`font-sub font-bold uppercase tracking-wider px-6 py-3 rounded-full transition-colors ${
            s.is_live ? "bg-red-600 text-white" : "bg-green text-black"
          }`}
        >
          {s.is_live ? "● We are LIVE — end stream" : "Go live"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
        <div className="md:col-span-2">
          <label className="label block mb-2">Stream title</label>
          <input data-testid="live-title" className="field" value={s.title || ""} onChange={(e) => set("title", e.target.value)} />
        </div>

        <div>
          <label className="label block mb-2">Live platform</label>
          <select data-testid="live-platform" className="field !border-b" value={s.platform} onChange={(e) => set("platform", e.target.value)}>
            <option value="youtube" className="bg-black">YouTube (with live chat)</option>
            <option value="facebook" className="bg-black">Facebook</option>
          </select>
        </div>
        <div className="flex items-end gap-3 pb-2">
          <Toggle testid="live-showchat" checked={!!s.show_chat} onChange={(v) => set("show_chat", v)} label="Show chat" />
          <span className="font-sub text-zinc-400">Show live chat (YouTube)</span>
        </div>

        <div className="md:col-span-2">
          <label className="label block mb-2">Current live stream link (paste your YouTube/Facebook live URL)</label>
          <input data-testid="live-stream-url" className="field" placeholder="https://www.youtube.com/watch?v=… or youtu.be/…" value={s.stream_url || ""} onChange={(e) => set("stream_url", e.target.value)} />
        </div>

        <div>
          <label className="label block mb-2">Offline replay platform</label>
          <select data-testid="live-offline-platform" className="field !border-b" value={s.offline_platform} onChange={(e) => set("offline_platform", e.target.value)}>
            <option value="youtube" className="bg-black">YouTube</option>
            <option value="facebook" className="bg-black">Facebook</option>
          </select>
        </div>
        <div>
          <label className="label block mb-2">Most recent stream / highlight link (shown when offline)</label>
          <input data-testid="live-offline-url" className="field" placeholder="https://www.youtube.com/watch?v=…" value={s.offline_url || ""} onChange={(e) => set("offline_url", e.target.value)} />
        </div>

        <div className="md:col-span-2 mt-2">
          <p className="label mb-3">Social channels (shown as “watch on” buttons)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
            {SOCIAL_FIELDS.map(([key, ph]) => (
              <input
                key={key}
                data-testid={`live-social-${key}`}
                className="field"
                placeholder={ph}
                value={s.socials?.[key] || ""}
                onChange={(e) => setSocial(key, e.target.value)}
              />
            ))}
          </div>
          <p className="font-sub text-xs text-zinc-600 mt-3">
            Note: TikTok & Instagram don't allow embedding live video, so these show as “watch live on…” buttons. YouTube & Facebook embed directly on the site.
          </p>
        </div>
      </div>

      <button
        data-testid="live-save"
        onClick={() => save()}
        disabled={saving}
        className="btn-fill mt-8 border border-green text-green font-sub font-semibold text-lg px-8 py-3.5 rounded-full flex items-center gap-2 disabled:opacity-60"
      >
        {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={18} />}
        {saving ? "Saving…" : "Save live settings"}
      </button>
    </section>
  );
};
